import { DialogComponent } from '@theme/dialog';

/**
 * A global popup used to request a special order for a sold-out size.
 *
 * A single instance lives on every page (see snippets/inquire-modal.liquid). Any button anywhere
 * can open it via the declarative action system, e.g.:
 *   on:click="#inquire-dialog/open?product_title=...&product_vendor=..."
 * or from JS: `document.getElementById('inquire-dialog')?.open({ product_title, product_vendor })`.
 *
 * The form submits natively (a real POST + full-page reload) — Shopify's contact form has no
 * supported AJAX/JSON endpoint, and fetch-submitting it gets challenged by Shopify's own bot
 * protection ("Your connection needs to be verified..."), so don't try to intercept the submit.
 * Instead, after the reload, `connectedCallback` checks whether Shopify actually rendered a
 * success/error message into this popup for this page load, and reopens the dialog to show it.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLTextAreaElement} body
 *
 * @extends DialogComponent<Refs>
 */
export class InquireDialog extends DialogComponent {
  requiredRefs = ['dialog', 'body'];

  /** @type {string} */
  #subjectPrefix = '';

  connectedCallback() {
    super.connectedCallback();

    const message = this.querySelector('#inquire-modal-message');
    if (message && message.children.length > 0) {
      this.showDialog();

      // Drop Shopify's ?contact_posted=true#... from the URL (without disturbing any other
      // params, e.g. ?variant=...) so a later manual refresh doesn't reopen the dialog on a
      // stale result.
      const url = new URL(location.href);
      url.searchParams.delete('contact_posted');
      url.hash = '';
      history.replaceState(null, '', `${url.pathname}${url.search}`);
    }
  }

  /**
   * Opens the popup, silently pre-loading the special-order subject for the given product.
   *
   * @param {{ product_title?: string, product_vendor?: string }} data
   */
  open = (data) => {
    const title = data?.product_title ?? '';
    const vendor = data?.product_vendor ?? '';

    this.#subjectPrefix = title || vendor ? `Special order - ${title} - ${vendor}` : '';

    this.showDialog();
  };

  /**
   * Prepends the special-order subject to the message before the native form submit.
   * Does not call `preventDefault` — the form posts normally, like the rest of the theme's contact form.
   *
   * @param {SubmitEvent} event
   */
  handleSubmit = (event) => {
    if (!this.#subjectPrefix) return;

    const { body } = this.refs;
    body.value = `${this.#subjectPrefix}\n\n${body.value}`.trim();
  };
}

if (!customElements.get('inquire-dialog')) {
  customElements.define('inquire-dialog', InquireDialog);
}
